import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Bell, CheckCheck } from "lucide-react";

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export default function NotificationBell() {
  const [items, setItems] = useState([]);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  const load = useCallback(() => {
    api.get("/notifications").then((r) => { setItems(r.data.notifications || []); setUnread(r.data.unread || 0); }).catch(() => {});
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 30000);
    return () => clearInterval(id);
  }, [load]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const openItem = async (n) => {
    setOpen(false);
    if (!n.read) { try { await api.post(`/notifications/${n.id}/read`); } catch {} load(); }
    if (n.link) navigate(n.link);
  };

  const markAll = async () => { try { await api.post("/notifications/read-all"); } catch {} load(); };

  return (
    <div className="relative" ref={boxRef}>
      <button onClick={() => setOpen((o) => !o)} data-testid="notif-bell-btn" title="Notifications"
        className="relative h-9 w-9 grid place-items-center rounded-full bg-secondary hover:bg-accent transition-colors">
        <Bell className="h-4 w-4" />
        {unread > 0 && (
          <span data-testid="notif-unread-badge" className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-primary text-primary-foreground text-[10px] font-bold">
            {unread > 9 ? "9+" : unread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-2 w-80 max-h-96 overflow-auto rounded-2xl bg-card border border-border shadow-xl z-50" data-testid="notif-dropdown">
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <span className="font-semibold text-sm">Notifications</span>
            {unread > 0 && (
              <button onClick={markAll} data-testid="notif-mark-all-btn" className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:underline">
                <CheckCheck className="h-3.5 w-3.5" /> Mark all read
              </button>
            )}
          </div>
          {items.length === 0 ? (
            <p className="px-4 py-6 text-sm text-muted-foreground text-center" data-testid="notif-empty">You're all caught up.</p>
          ) : (
            items.map((n) => (
              <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
                className={`w-full text-left px-4 py-3 border-b border-border last:border-0 hover:bg-secondary transition-colors ${n.read ? "" : "bg-accent/40"}`}>
                <div className="flex items-start gap-2">
                  {!n.read && <span className="mt-1.5 h-2 w-2 rounded-full bg-primary shrink-0" />}
                  <div className={n.read ? "pl-4" : ""}>
                    <p className="text-sm font-semibold leading-snug">{n.title}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-snug">{n.body}</p>
                    <p className="text-[10px] text-muted-foreground mt-1">{timeAgo(n.created_at)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
